import { useCallback, useEffect, useState } from "react";
import {
  Ban, BadgePercent, Building2, CheckCircle2, DoorOpen, HelpCircle, Lightbulb, Loader2, LogOut,
  Mail, Send, Trash2, TrendingUp, Users,
} from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AccountSecurity } from "@/components/AccountSecurity";
import { AdminHealth } from "@/components/AdminHealth";
import { DiscountPanel } from "@/components/DiscountPanel";
import { PasskeyPanel } from "@/components/PasskeyPanel";
import { StripePricesCard } from "@/components/StripePricesCard";
import { StoreApps } from "@/components/StoreApps";
import { CommunityAdmin } from "@/components/CommunityAdmin";
import { BlogStudio } from "@/components/BlogStudio";
import { AiModelPanel } from "@/components/AiModelPanel";

const PLANS = [
  ["free", "Free"], ["starter", "Starter"], ["professional", "Professional"],
  ["business", "Business / Branded"], ["enterprise", "Enterprise"],
];

const DOMAIN_STATES = {
  requested: { label: "requested", hint: "We have your request — support sets up DNS and SSL, usually within a business day." },
  in_progress: { label: "being set up", hint: "Support is wiring this up now. Add the DNS record below if you have not already." },
  active: { label: "live", hint: "Your domain is live and serving your booking page over HTTPS." },
  declined: { label: "not set up", hint: "Support could not complete this one — see the note below." },
};

const Metric = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-2 font-display text-2xl text-slate-50">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [trials, setTrials] = useState(null);
  const [domains, setDomains] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState({ subject: "", message: "" });
  const [confirmName, setConfirmName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [feedFilter, setFeedFilter] = useState({ kind: "", status: "" });
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState("");
  const [exits, setExits] = useState(null);
  const [discounts, setDiscounts] = useState([]);

  const load = useCallback(async () => {
    setError("");
    try {
      const [o, c, t, d, f, x, dis] = await Promise.all([api.get("/admin/overview"), api.get("/admin/companies"),
        api.get("/admin/trials"), api.get("/admin/domain-requests"),
        api.get("/admin/feedback", { params: feedFilter }), api.get("/admin/exit-surveys"),
        api.get("/admin/discounts")]);
      setOverview(o.data);
      setCompanies(c.data.companies);
      setTrials(t.data);
      setDomains(d.data);
      setFeedback(f.data);
      setExits(x.data);
      setDiscounts(dis.data.discounts);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [feedFilter]);

  useEffect(() => { load(); }, [load]);

  const pendingDomains = (domains?.requests || [])
    .filter((r) => r.status === "requested" || r.status === "in_progress").length;

  const act = async (fn, message) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(message);
      await load();
      if (detail) {
        try {
          setDetail((await api.get(`/admin/companies/${detail.company.id}`)).data);
        } catch {
          setDetail(null);  // the company is gone — close the drawer
        }
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const rows = companies.filter((c) =>
    !query || `${c.name} ${c.email} ${c.plan_name}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div data-testid="admin-console" className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex min-h-16 max-w-[1500px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:flex-nowrap sm:py-0 sm:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <img src="/limolinkx-logo.png" alt={PLATFORM_NAME}
              className="h-9 w-9 shrink-0 rounded bg-white/95 object-contain p-0.5" />
            <p className="font-display text-lg font-semibold text-slate-100">{PLATFORM_NAME}</p>
            <span className="rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-sky-300">
              Platform owner
            </span>
            {overview?.test_mode && (
              <span data-testid="admin-test-mode" className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-300">
                Stripe test mode
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-4">
            <span className="hidden font-mono text-xs text-slate-500 sm:inline">{user?.email}</span>
            <Button data-testid="admin-logout" variant="ghost" size="sm"
              onClick={async () => { await logout(); window.location.href = "/admin"; }}
              className="text-slate-400 hover:text-gold">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-8 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap gap-2">
          {[["overview", "Revenue & growth"], ["subscribers", "Subscribers"],
            ["trials", "Trial watchlist"], ["discounts", "Discounts"], ["domains", "Domains"],
            ["feedback", "Feedback inbox"], ["exits", "Why they left"], ["broadcast", "Broadcast"],
            ["health", "System health"],
            ["store", "Store apps"],
            ["communities", "Communities"],
            ["blog", "Blog"],
            ["ai", "AI models"],
            ["account", "Your login"]]
            .map(([k, l]) => (
              <button key={k} data-testid={`admin-tab-${k}`}
                onClick={() => { setTab(k); setDetail(null); setNotice(""); setError(""); }}
                className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                  tab === k ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                {l}
                {k === "domains" && pendingDomains > 0 && (
                  <span data-testid="admin-domains-badge"
                    className="ml-2 rounded-full bg-gold px-1.5 py-0.5 text-[10px] text-ink-900">
                    {pendingDomains}
                  </span>
                )}
                {k === "feedback" && feedback?.counts?.new > 0 && (
                  <span data-testid="admin-feedback-badge"
                    className="ml-2 rounded-full bg-gold px-1.5 py-0.5 text-[10px] text-ink-900">
                    {feedback.counts.new}
                  </span>
                )}
              </button>
            ))}
        </div>

        {error && <p data-testid="admin-error" className="text-sm text-red-400">{error}</p>}
        {notice && <p data-testid="admin-notice" className="text-sm text-emerald-400">{notice}</p>}

        {tab === "overview" && overview && (
          <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-600 bg-ink-800 p-5">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Monday summary</p>
                <p className="mt-1.5 text-sm text-slate-400">
                  Emailed to {user?.email} every Monday at 8am US Central — new signups, revenue and
                  anything waiting in your feedback inbox.
                </p>
              </div>
              <Button data-testid="owner-digest-send" variant="outline" disabled={busy}
                onClick={() => act(async () => {
                  const { data } = await api.post("/admin/owner-digest/send");
                  setNotice(`Summary emailed to ${data.to}.`);
                }, "")}
                className="border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send it to me now"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Metric testId="admin-mrr" label="MRR" value={money(overview.mrr)}
                sub={`${money(overview.arr)} a year`} />
              <Metric testId="admin-collected" label="Collected this month" value={money(overview.collected_this_month)} />
              <Metric testId="admin-paying" label="Paying accounts" value={overview.paying}
                sub={`${overview.companies} signed up · ${overview.conversion_rate}% converted${
                  overview.comped ? ` · ${overview.comped} comped (not counted)` : ""}`} />
              <Metric testId="admin-arpa" label="Average per account" value={money(overview.arpa)}
                sub={`${overview.annual_accounts} on annual billing${
                  overview.prepaid_accounts > overview.annual_accounts
                    ? ` · ${overview.prepaid_accounts} paying ahead` : ""}`} />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <StripePricesCard />
              <div className="min-w-0 rounded-xl border border-ink-600 bg-ink-800 p-6">
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Plan mix</p>
                <div className="mt-4 space-y-3" data-testid="admin-plan-mix">
                  {overview.by_plan.map((p) => (
                    <div key={p.key} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">{p.name}</span>
                      <span className="flex items-center gap-4">
                        <span className="font-mono text-xs text-slate-500">{p.count} account(s)</span>
                        <span className="text-slate-100">{money(p.mrr)}/mo</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                  <TrendingUp className="h-3.5 w-3.5" /> Signups · last 8 weeks
                </p>
                <div className="mt-6 flex h-32 items-end gap-2" data-testid="admin-signups">
                  {overview.signups_by_week.map((w) => {
                    const max = Math.max(1, ...overview.signups_by_week.map((x) => x.signups));
                    return (
                      <div key={w.week} className="flex flex-1 flex-col items-center justify-end gap-2">
                        <span className="font-mono text-[10px] text-slate-400">{w.signups || ""}</span>
                        <div className={`w-full rounded-t ${w.signups ? "bg-gold/70" : "bg-ink-600"}`}
                          style={{ height: `${w.signups ? 14 + (w.signups / max) * 70 : 3}px` }} />
                        <span className="font-mono text-[9px] text-slate-600">{w.week.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <InstallPrompt companyName="the owner console" testId="admin-install-prompt"
              blurb="Install this console on your phone or desktop and your whole business — MRR, new signups, every subscriber — is one tap away." />

            <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
                Churn & downgrades · {overview.churn.length}
              </p>
              <div className="mt-4 space-y-2" data-testid="admin-churn">
                {overview.churn.length === 0 && (
                  <p className="text-sm text-slate-500">Nobody has been downgraded or cancelled.</p>
                )}
                {overview.churn.map((c) => (
                  <p key={c.id} className="text-sm text-slate-300">
                    <span className="text-slate-100">{c.name}</span> · {c.reason} · {c.since}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "subscribers" && !detail && (
          <div className="space-y-4">
            <Input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by company, email or plan"
              className="h-11 max-w-md border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
            <div className="overflow-x-auto rounded-xl border border-ink-600 bg-ink-800">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="p-4">Company</th><th className="p-4">Plan</th>
                    <th className="p-4 text-right">Per month</th><th className="p-4 text-right">Trips</th>
                    <th className="p-4 text-right">Team</th><th className="p-4">Since</th>
                    <th className="p-4">Status</th><th className="p-4" />
                  </tr>
                </thead>
                <tbody data-testid="admin-companies">
                  {rows.map((c) => (
                    <tr key={c.id} className="border-b border-ink-700/60">
                      <td className="p-4">
                        <p className="text-slate-100">{c.name}</p>
                        <p className="font-mono text-[11px] text-slate-500">{c.email || "no email"}</p>
                      </td>
                      <td className="p-4 text-slate-300">
                        {c.plan_name}
                        <span className="ml-2 font-mono text-[10px] uppercase text-slate-600">{c.billing_cycle}</span>
                      </td>
                      <td className="p-4 text-right text-slate-100">{money(c.monthly_value)}</td>
                      <td className="p-4 text-right text-slate-300">{c.trips}</td>
                      <td className="p-4 text-right text-slate-300">{c.users}</td>
                      <td className="p-4 font-mono text-[11px] text-slate-500">{(c.plan_since || "").slice(0, 10)}</td>
                      <td className="p-4">
                        {c.suspended ? (
                          <span data-testid={`admin-suspended-${c.id}`} className="font-mono text-[10px] uppercase tracking-widest text-red-400">Suspended</span>
                        ) : c.subscribed ? (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">Subscribed</span>
                        ) : c.comped ? (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-sky-300">Comped</span>
                        ) : c.had_subscription ? (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Lapsed</span>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Free</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <button data-testid={`admin-open-${c.id}`}
                          onClick={async () => setDetail((await api.get(`/admin/companies/${c.id}`)).data)}
                          className="font-mono text-[11px] uppercase tracking-widest text-gold">Open →</button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="p-10 text-center">
                      <Building2 className="mx-auto h-6 w-6 text-slate-600" />
                      <p data-testid="admin-companies-empty" className="mt-3 text-sm text-slate-500">No accounts match that search.</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "subscribers" && detail && (
          <div data-testid="admin-company-detail" className="space-y-6">
            <button data-testid="admin-detail-back" onClick={() => setDetail(null)}
              className="font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
              ← All subscribers
            </button>

            <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-50">{detail.company.name}</h1>
                  <p className="mt-1 text-sm text-slate-400">
                    {detail.company.email || "no email"} · {detail.company.phone || "no phone"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {[detail.address, detail.timezone].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl text-slate-50">{detail.company.plan_name}</p>
                  <p className="font-mono text-xs text-slate-500">
                    {money(detail.company.monthly_value)}/mo · since {(detail.company.plan_since || "").slice(0, 10)}
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
                {[["Trips", detail.company.trips], ["Airport manifests", detail.airport_manifests],
                  ["Chauffeurs", detail.chauffeurs], ["Vehicles", detail.vehicles],
                  ["Team", detail.company.users]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-ink-600 bg-ink-900/50 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
                    <p className="mt-1.5 font-display text-xl text-slate-50">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Owner actions</p>
              <p className="mt-2 text-xs text-slate-400">
                Changing the plan here does not touch Stripe — use it to comp an account or fix a mistake.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {PLANS.map(([key, label]) => (
                  <Button key={key} data-testid={`admin-set-plan-${key}`} size="sm" disabled={busy || detail.company.plan === key}
                    onClick={() => act(() => api.post(`/admin/companies/${detail.company.id}/plan`,
                      { plan: key, note: "Set from the platform console" }), `${detail.company.name} moved to ${label}.`)}
                    className={detail.company.plan === key ? "bg-ink-600 text-slate-400"
                      : "bg-sky-500/20 text-sky-100 hover:bg-sky-500/30"}>
                    {label}
                  </Button>
                ))}
              </div>
              <div className="mt-5">
                {detail.company.suspended ? (
                  <Button data-testid="admin-restore" disabled={busy}
                    onClick={() => act(() => api.post(`/admin/companies/${detail.company.id}/suspend`,
                      { suspended: false }), `${detail.company.name} restored.`)}
                    className="bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30">
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Restore access
                  </Button>
                ) : (
                  <Button data-testid="admin-suspend" disabled={busy}
                    onClick={() => act(() => api.post(`/admin/companies/${detail.company.id}/suspend`,
                      { suspended: true, reason: "Suspended by the platform owner" }),
                      `${detail.company.name} suspended.`)}
                    className="bg-red-500/20 text-red-100 hover:bg-red-500/30">
                    <Ban className="mr-2 h-4 w-4" /> Suspend this account
                  </Button>
                )}
                {detail.company.suspended && (
                  <p className="mt-2 text-xs text-red-300">
                    Suspended: {detail.company.suspend_reason || "no reason recorded"}
                  </p>
                )}
              </div>

              <DiscountPanel company={detail.company} busy={busy} act={act} />

              <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/5 p-4" data-testid="admin-delete-panel">
                <p className="font-mono text-[10px] uppercase tracking-widest text-red-300">Delete this company</p>
                <p className="mt-2 text-xs text-slate-400">
                  Permanently removes the company, its logins, trips, airport manifests, invoices,
                  vehicles, chauffeurs, partners and alerts. This cannot be undone — suspend instead
                  if you only want to lock them out.
                </p>
                <Input
                  data-testid="admin-delete-confirm"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={`Type "${detail.company.name}" to confirm`}
                  className="mt-3 h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-red-400"
                />
                <Button
                  data-testid="admin-delete-company"
                  disabled={busy || confirmName.trim().toLowerCase() !== detail.company.name.trim().toLowerCase()}
                  onClick={() => act(async () => {
                    await api.delete(`/admin/companies/${detail.company.id}`, {
                      data: { confirm_name: confirmName, reason: "Deleted from the platform console" },
                    });
                    setDetail(null);
                    setConfirmName("");
                  }, `${detail.company.name} deleted for good.`)}
                  className="mt-3 bg-red-500/20 text-red-100 hover:bg-red-500/30 disabled:opacity-40"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete permanently
                </Button>
              </div>
              {detail.actions?.length > 0 && (                <div className="mt-6 border-t border-ink-600 pt-4" data-testid="admin-action-log">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Action log</p>
                  <div className="mt-2 space-y-1">
                    {detail.actions.map((a, i) => (
                      <p key={`${a.at}-${i}`} className="font-mono text-[11px] text-slate-500">
                        {a.at} · {a.action}{a.detail ? ` · ${a.detail}` : ""} · {a.by}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                  <Users className="h-3.5 w-3.5" /> Team
                </p>
                <div className="mt-4 space-y-2" data-testid="admin-detail-team">
                  {detail.team.map((t) => (
                    <p key={t.email} className="text-sm text-slate-300">
                      {t.name || t.email} <span className="font-mono text-[10px] uppercase text-slate-600">{t.role}</span>
                    </p>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Payments</p>
                <div className="mt-4 space-y-2" data-testid="admin-detail-payments">
                  {detail.payments.length === 0 && (
                    <p className="text-sm text-slate-500">No payments taken yet.</p>
                  )}
                  {detail.payments.map((p, i) => (
                    <p key={`${p.date}-${i}`} className="flex justify-between text-sm text-slate-300">
                      <span>{p.date} · {p.lookup_key || "purchase"}</span>
                      <span className="text-slate-100">{money(p.amount)}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "discounts" && (
          <div data-testid="admin-discounts" className="space-y-4">
            <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                <BadgePercent className="h-3.5 w-3.5" /> Discounts in force
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Every discount here is a real Stripe coupon on that operator's subscription — they see
                it on their own billing screen. Give a new one from any company in Subscribers.
              </p>
              {discounts.length === 0 ? (
                <p data-testid="admin-discounts-empty" className="mt-4 text-sm text-slate-500">
                  No discounts yet. Open a company in Subscribers and use “Give discount”.
                </p>
              ) : (
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="p-4">Company</th><th className="p-4">Plan</th>
                        <th className="p-4">Discount</th><th className="p-4">Live on subscription</th>
                        <th className="p-4">Given</th><th className="p-4" />
                      </tr>
                    </thead>
                    <tbody data-testid="admin-discounts-table">
                      {discounts.map((d) => (
                        <tr key={d.id} className="border-b border-ink-700/60">
                          <td className="p-4">
                            <p className="text-slate-100">{d.name}</p>
                            <p className="font-mono text-[11px] text-slate-500">{d.email}</p>
                          </td>
                          <td className="p-4 text-slate-300">{d.plan_name}</td>
                          <td className="p-4 text-emerald-300">
                            {d.label}
                            {d.note && <span className="block text-[11px] text-slate-500">{d.note}</span>}
                          </td>
                          <td className="p-4 font-mono text-[11px] text-slate-400">
                            {d.applied_to_subscription ? "yes" : "at next checkout"}
                          </td>
                          <td className="p-4 font-mono text-[11px] text-slate-500">{d.at} · {d.by}</td>
                          <td className="p-4 text-right">
                            <Button data-testid={`admin-discount-remove-${d.id}`} size="sm" disabled={busy}
                              onClick={() => act(() => api.delete(`/admin/companies/${d.id}/discount`),
                                `Discount removed from ${d.name}.`)}
                              className="bg-red-500/20 text-red-100 hover:bg-red-500/30">
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "trials" && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <Metric testId="trials-at-limit" label="At their limit" value={trials?.at_limit ?? "—"}
                sub="ready to upgrade today" />
              <Metric testId="trials-nearly" label="Over 70% used" value={trials?.nearly ?? "—"}
                sub="worth a nudge this week" />
              <Metric testId="trials-quiet" label="Never booked a trip" value={trials?.quiet ?? "—"}
                sub="signed up but never started" />
            </div>

            <div className="overflow-x-auto rounded-xl border border-ink-600 bg-ink-800">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="p-4">Account</th><th className="p-4">Free trips used</th>
                    <th className="p-4">Airport manifests</th><th className="p-4 text-right">Age</th>
                    <th className="p-4">Last trip</th><th className="p-4" />
                  </tr>
                </thead>
                <tbody data-testid="trials-table">
                  {(trials?.trials || []).map((t) => (
                    <tr key={t.id} className="border-b border-ink-700/60">
                      <td className="p-4">
                        <p className="text-slate-100">{t.name}</p>
                        <p className="font-mono text-[11px] text-slate-500">{t.owner_email || "no owner email"}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="h-1.5 w-28 overflow-hidden rounded-full bg-ink-600">
                            <div className={`h-full rounded-full ${t.trip_pct >= 100 ? "bg-red-400"
                              : t.trip_pct >= 70 ? "bg-amber-400" : "bg-gold/70"}`}
                              style={{ width: `${Math.min(100, t.trip_pct)}%` }} />
                          </div>
                          <span className="font-mono text-[11px] text-slate-400">
                            {t.trips_used}/{t.trip_limit}
                          </span>
                        </div>
                        <p className="mt-1 font-mono text-[10px] text-slate-600">
                          {t.trips_this_month} booked this month
                        </p>
                      </td>
                      <td className="p-4 font-mono text-[11px] text-slate-400">
                        {t.manifests_used}/{t.manifest_limit}
                      </td>
                      <td className="p-4 text-right font-mono text-[11px] text-slate-400">{t.days_since_signup}d</td>
                      <td className="p-4 font-mono text-[11px] text-slate-500">{t.last_activity || "never"}</td>
                      <td className="p-4 text-right">
                        <button data-testid={`trial-nudge-${t.id}`} disabled={busy || !t.owner_email}
                          onClick={() => act(() => api.post(`/admin/companies/${t.id}/nudge`),
                            `Upgrade nudge sent to ${t.owner_email}.`)}
                          className="font-mono text-[11px] uppercase tracking-widest text-gold disabled:text-slate-600">
                          {t.nudged_at ? `Nudge again · ${t.nudged_at}` : "Nudge to upgrade"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {trials?.trials?.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-500"
                      data-testid="trials-empty">No free accounts right now.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "domains" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Operators on Business and up can ask for their own hostname. Point it at your deployment
              (CNAME → your app host), link it in Manage Publishes → Domain, then mark it live here.
            </p>
            <div className="overflow-x-auto rounded-xl border border-ink-600 bg-ink-800">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="p-4">Company</th><th className="p-4">Requested domain</th>
                    <th className="p-4">Branded link</th><th className="p-4">Status</th>
                    <th className="p-4">Asked</th><th className="p-4" />
                  </tr>
                </thead>
                <tbody data-testid="domain-requests">
                  {(domains?.requests || []).map((r) => (
                    <tr key={r.id} className="border-b border-ink-700/60">
                      <td className="p-4">
                        <p className="text-slate-100">{r.name}</p>
                        <p className="font-mono text-[11px] text-slate-500">{r.plan}</p>
                      </td>
                      <td className="p-4 font-mono text-[12px] text-slate-200">{r.domain}</td>
                      <td className="p-4 font-mono text-[11px] text-slate-500">/ride/{r.handle || r.id}</td>
                      <td className="p-4">
                        <span data-testid={`domain-status-${r.id}`}
                          className={`font-mono text-[10px] uppercase tracking-widest ${
                            r.status === "active" ? "text-emerald-300"
                              : r.status === "declined" ? "text-red-400"
                              : r.status === "in_progress" ? "text-sky-300" : "text-amber-300"}`}>
                          {DOMAIN_STATES[r.status]?.label || r.status}
                        </span>
                        {r.note && <p className="mt-1 text-[11px] text-slate-500">{r.note}</p>}
                      </td>
                      <td className="p-4 font-mono text-[11px] text-slate-500">{r.requested_at}</td>
                      <td className="p-4 text-right">
                        <div className="flex flex-wrap justify-end gap-3">
                          {["in_progress", "active", "declined"].map((s) => (
                            <button key={s} data-testid={`domain-set-${s}-${r.id}`} disabled={busy || r.status === s}
                              onClick={() => act(() => api.post(`/admin/companies/${r.id}/domain`,
                                { status: s, note: window.prompt(
                                    `Note for ${r.name} (they will see this):`, r.note || "") || r.note || "" }),
                                `${r.domain} marked ${DOMAIN_STATES[s].label}.`)}
                              className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                                r.status === s ? "border-ink-600 text-slate-500"
                                  : "border-gold/40 text-gold hover:bg-gold/10"}`}>
                              {DOMAIN_STATES[s].label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {domains?.requests?.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-500"
                      data-testid="domains-empty">Nobody has asked for their own domain yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "feedback" && (
          <div data-testid="feedback-inbox" className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric testId="feedback-count-new" label="Waiting on you" value={feedback?.counts?.new ?? 0} />
              <Metric testId="feedback-count-progress" label="In progress" value={feedback?.counts?.in_progress ?? 0} />
              <Metric testId="feedback-count-help" label="Help requests" value={feedback?.counts?.help ?? 0} />
              <Metric testId="feedback-count-suggestion" label="Suggestions" value={feedback?.counts?.suggestion ?? 0} />
            </div>

            <div className="flex flex-wrap gap-2">
              {[["", "Everything"], ["help", "Help only"], ["suggestion", "Suggestions only"]].map(([k, l]) => (
                <button key={k || "all"} data-testid={`feedback-filter-kind-${k || "all"}`}
                  onClick={() => setFeedFilter({ ...feedFilter, kind: k })}
                  className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    feedFilter.kind === k ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {l}
                </button>
              ))}
              <span className="mx-1 w-px bg-ink-600" />
              {[["", "Any status"], ["new", "New"], ["in_progress", "In progress"], ["done", "Done"]].map(([k, l]) => (
                <button key={k || "any"} data-testid={`feedback-filter-status-${k || "any"}`}
                  onClick={() => setFeedFilter({ ...feedFilter, status: k })}
                  className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    feedFilter.status === k ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {l}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {feedback?.feedback?.map((f) => (
                <div key={f.id} data-testid={`feedback-row-${f.id}`}
                  className="rounded-xl border border-ink-600 bg-ink-800 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {f.kind === "help"
                        ? <HelpCircle className="h-4 w-4 text-sky-300" />
                        : <Lightbulb className="h-4 w-4 text-gold" />}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        {f.kind === "help" ? "Help request" : "Suggestion"} · {f.app}
                      </span>
                    </div>
                    <span data-testid={`feedback-status-${f.id}`}
                      className={`font-mono text-[10px] uppercase tracking-widest ${
                        f.status === "done" ? "text-emerald-300"
                          : f.status === "in_progress" ? "text-sky-300" : "text-amber-300"}`}>
                      {f.status === "in_progress" ? "in progress" : f.status}
                    </span>
                  </div>

                  <p className="mt-3 whitespace-pre-wrap text-sm text-slate-100">{f.message}</p>

                  <p className="mt-3 font-mono text-[11px] text-slate-500">
                    {f.user_name} · {f.user_email} · {f.role}
                    {f.company_name ? ` · ${f.company_name}` : ""} · {f.at}
                    {f.page ? ` · ${f.page}` : ""}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-3">
                    {["new", "in_progress", "done"].map((s) => (
                      <button key={s} data-testid={`feedback-set-${s}-${f.id}`} disabled={busy || f.status === s}
                        onClick={() => act(() => api.post(`/admin/feedback/${f.id}/status`, { status: s }),
                          "Status updated.")}
                        className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                          f.status === s ? "border-ink-600 text-slate-500" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
                        {s === "in_progress" ? "In progress" : s}
                      </button>
                    ))}
                    <button data-testid={`feedback-reply-open-${f.id}`}
                      onClick={() => { setReplyTo(replyTo === f.id ? null : f.id); setReply(""); }}
                      className="rounded-full border border-ink-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:text-gold">
                      Reply by email
                    </button>
                  </div>

                  {f.replied_at && (
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-emerald-400/70">
                      Replied {f.replied_at}
                    </p>
                  )}

                  {replyTo === f.id && (
                    <div className="mt-4 space-y-3 border-t border-ink-600 pt-4">
                      <Textarea data-testid={`feedback-reply-message-${f.id}`} rows={4} value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder={`Write back to ${f.user_name || f.user_email}. They get it by email.`}
                        className="border-ink-600 bg-ink-900 text-sm text-slate-100 focus-visible:ring-gold" />
                      <Button data-testid={`feedback-reply-send-${f.id}`} size="sm"
                        disabled={busy || reply.trim().length < 3}
                        onClick={() => act(async () => {
                          await api.post(`/admin/feedback/${f.id}/reply`, { message: reply });
                          setReplyTo(null);
                          setReply("");
                        }, `Reply sent to ${f.user_email}.`)}
                        className="bg-gold text-ink-900 hover:bg-gold-light">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-2 h-4 w-4" /> Send reply</>)}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {feedback?.feedback?.length === 0 && (
                <p data-testid="feedback-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-10 text-center text-sm text-slate-500">
                  Nothing here yet. Help requests and suggestions from the dispatch, chauffeur and
                  passenger apps land in this inbox.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "exits" && (
          <div data-testid="exit-surveys" className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric testId="exit-count-total" label="Accounts closed" value={exits?.counts?.total ?? 0} />
              <Metric testId="exit-count-paying" label="Were paying" value={exits?.counts?.paying ?? 0} />
              <Metric testId="exit-count-reason" label="Told you why" value={exits?.counts?.with_reason ?? 0} />
              <Metric testId="exit-count-immediate" label="Left immediately" value={exits?.counts?.immediate ?? 0}
                sub="the rest used the 30-day grace" />
            </div>

            <div className="space-y-4">
              {exits?.exits?.map((x) => (
                <div key={x.id} data-testid={`exit-row-${x.id}`}
                  className="rounded-xl border border-ink-600 bg-ink-800 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <DoorOpen className="h-4 w-4 text-red-300" />
                      <span className="text-sm text-slate-100">{x.company_name}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                        {x.plan}{x.was_paying ? " · was paying" : ""}
                      </span>
                    </div>
                    <span data-testid={`exit-mode-${x.id}`}
                      className={`font-mono text-[10px] uppercase tracking-widest ${
                        x.mode === "immediate" ? "text-red-400" : "text-amber-300"}`}>
                      {x.mode === "immediate" ? "deleted immediately" : "30-day grace"}
                    </span>
                  </div>

                  {x.reason ? (
                    <p className="mt-3 whitespace-pre-wrap text-sm text-slate-100">“{x.reason}”</p>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">They did not give a reason.</p>
                  )}

                  <p className="mt-3 font-mono text-[11px] text-slate-500">
                    {x.owner_email} · {x.trips} trip(s) run
                    {x.signed_up_at ? ` · joined ${x.signed_up_at}` : ""} · closed {x.at}
                  </p>
                </div>
              ))}
              {exits?.exits?.length === 0 && (
                <p data-testid="exits-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-10 text-center text-sm text-slate-500">
                  Nobody has closed their account. When someone does, the reason they gave lands here —
                  and it is kept even after their data is erased.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "health" && <AdminHealth />}
        {tab === "store" && <StoreApps />}
        {tab === "communities" && <CommunityAdmin />}
        {tab === "blog" && <BlogStudio scope="admin" />}
        {tab === "ai" && <AiModelPanel />}

        {tab === "account" && (
          <div className="max-w-2xl space-y-6">
            <div className="rounded-xl border border-ink-600 bg-ink-800 p-5">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
                Face ID unlock
              </p>
              <div className="mt-3">
                <PasskeyPanel testId="admin-passkeys" />
              </div>
            </div>
            <AccountSecurity />
          </div>
        )}

        {tab === "broadcast" && (
          <div className="max-w-2xl space-y-5 rounded-xl border border-ink-600 bg-ink-800 p-6">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
              <Mail className="h-3.5 w-3.5" /> Message every operator
            </p>
            <p className="text-xs text-slate-400">
              Goes to the owner of every account. Use it for release notes, price changes or downtime.
            </p>
            <Input data-testid="broadcast-subject" value={note.subject}
              onChange={(e) => setNote({ ...note, subject: e.target.value })}
              placeholder="Subject"
              className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
            <Textarea data-testid="broadcast-message" rows={8} value={note.message}
              onChange={(e) => setNote({ ...note, message: e.target.value })}
              placeholder="Write your message. One blank line between paragraphs."
              className="border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
            <Button data-testid="broadcast-send" disabled={busy || !note.subject || !note.message}
              onClick={() => act(async () => {
                const { data } = await api.post("/admin/broadcast", note);
                setNote({ subject: "", message: "" });
                setNotice(`Sent to ${data.sent.length} operator(s)` +
                  (data.failed.length ? ` · ${data.failed.length} failed` : ""));
              }, "")}
              className="bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-2 h-4 w-4" /> Send to all operators</>)}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
